import { useMutation, useQuery } from 'react-query'
import { proxy, subscribe, useSnapshot } from 'valtio'

import type { components } from '@23/openapi'

import {
  authenticatedWebinarApiRequest,
  isApiError,
  publicWebinarApiRequest,
} from '../../global/api/api'
import { getLiveId } from '../../global/blocs/roomInfo'
import queryClient from '../../queryClient'

type ActionCardRequestData = components['schemas']['EditActionCardRequest']
type ActionCardResponse = components['schemas']['ActionCardResponse']
type ActionCardAnswerRequestData =
  components['schemas']['AnswerActionCardRequest']

type ActionCardsPersistedState = {
  answeredActionCards: Array<number>
}

const loadInitAnsweredState = () => {
  const raw = localStorage.getItem('answeredActionCards')
  if (!raw) return { answeredActionCards: [] }

  try {
    return JSON.parse(raw) as ActionCardsPersistedState
  } catch {
    return { answeredActionCards: [] }
  }
}

const answeredActionCardsState = proxy<ActionCardsPersistedState>(
  loadInitAnsweredState()
)

subscribe(answeredActionCardsState, () => {
  localStorage.setItem(
    'answeredActionCards',
    JSON.stringify(answeredActionCardsState)
  )
})

const storeAnswerLocally = (cardId: number) => {
  answeredActionCardsState.answeredActionCards.push(cardId)
}

const useIsActionCardAnswered = (cardId?: number) => {
  const { answeredActionCards } = useSnapshot(answeredActionCardsState)

  if (!cardId) return false
  return answeredActionCards.includes(cardId)
}

const queryKeys = {
  list: ['action-cards'],
  answerCount: (cardId?: number) => ['action-cards', 'answer-count', cardId],
  active: ['action-cards', 'active'],
}

const useActiveActionCardsQuery = () => {
  return useQuery({
    queryKey: queryKeys.active,
    queryFn: async () => {
      const { data } = await publicWebinarApiRequest(
        'action-cards/active',
        'get'
      )

      return data
    },
    enabled: !!getLiveId(),
    refetchInterval: 10e3,
  })
}

const useActionCardStateMutation = (actionCardId?: number) => {
  return useMutation({
    mutationFn: async (isActive: boolean) => {
      if (!actionCardId) return undefined

      const { data } = await authenticatedWebinarApiRequest(
        `action-cards/${actionCardId}/active`,
        'post',
        { active: isActive }
      )
      return data
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.active,
      })
    },
  })
}

const useCreateActionCardMutation = () => {
  return useMutation({
    mutationFn: async () => {
      const { data } = await authenticatedWebinarApiRequest(
        'action-cards',
        'post'
      )
      return data.id
    },
  })
}

const useEditActionCardMutation = (id?: number) => {
  return useMutation({
    mutationFn: async (opts: ActionCardRequestData) => {
      if (!id) return null
      const { data } = await authenticatedWebinarApiRequest(
        `action-cards/${id}/edit`,
        'post',
        opts
      )
      return data
    },
    onSettled: async (data, error) => {
      if (!data?.isActive) return

      if (error) {
        await queryClient.invalidateQueries({
          queryKey: queryKeys.active,
        })
      }

      queryClient.setQueryData(queryKeys.active, data)
    },
  })
}

const useAnswerActionCardMutation = (id?: number) => {
  return useMutation({
    mutationFn: async (opts: ActionCardAnswerRequestData) => {
      if (!id) return null
      const { data } = await publicWebinarApiRequest(
        `action-cards/${id}/answer`,
        'post',
        opts
      )

      return data
    },
    onSuccess: () => {
      if (!id) return
      storeAnswerLocally(id)
    },
    onError: (error) => {
      if (!id) return
      if (isApiError(error, 'action-cards/{id}/answer', 'post', 403)) {
        storeAnswerLocally(id)
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.active,
      })
    },
  })
}

const useDeleteActionCardMutation = (id?: number) => {
  return useMutation({
    mutationFn: async () => {
      if (!id) return undefined
      const { data } = await authenticatedWebinarApiRequest(
        `action-cards/${id}`,
        'delete'
      )
      return data
    },
    onSuccess: async () => {
      const activeCard = queryClient.getQueryData<ActionCardResponse>(
        queryKeys.active
      )

      if (activeCard?.id === id) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.active })
      }
    },
  })
}

const useActionCardAnswerCount = (id?: number) => {
  const query = useQuery({
    queryKey: queryKeys.answerCount(id),
    queryFn: () => {
      if (!id) return undefined

      return queryClient.getQueryData<number>(queryKeys.answerCount(id))
    },
    enabled: !!id,
    staleTime: 10e3,
  })

  const queryData = query.data
  if (queryData && !query.isStale) return queryData

  queryClient
    .fetchQuery({
      queryKey: queryKeys.list,
      queryFn: async () => {
        if (!id) return
        const { data: actionCards } = await authenticatedWebinarApiRequest(
          'action-cards',
          'get'
        )
        actionCards.forEach((card) => {
          queryClient.setQueryData(
            queryKeys.answerCount(card.id),
            card.answerCount
          )
        })
      },
    })
    .catch(() => {})

  return queryData ?? 0
}

export {
  type ActionCardResponse,
  useIsActionCardAnswered,
  useCreateActionCardMutation,
  useActiveActionCardsQuery,
  useEditActionCardMutation,
  useActionCardStateMutation,
  useDeleteActionCardMutation,
  useAnswerActionCardMutation,
  useActionCardAnswerCount,
}
